import classNames from 'classnames';
import { isSameDay } from 'date-fns/isSameDay';
import type { ReactDatePickerProps } from 'react-datepicker';
import dayStyles from '../Components/CalendarDay/CalendarDay.module.css';
import calendarStyles from '../DatePicker.module.css';
import { getContentLanguage } from '~/Features/Configuration/Utils/languageUtils';
import { getToday, mapToDateLocale } from '~/Partials/Utils/dateUtils';

export interface ConfigProps {
    selectedDay: Date | null;
    calendarClassName?: string;
    monthClassName?: string;
    weekDayClassName?: string;
    dayClassName?: string;
    shouldDayBeHidden?: (day: Date) => boolean;
    shouldDayBeDisabled?: (day: Date) => boolean;
    shouldDayBeHighlighted?: (day: Date) => boolean;
    showWeekNumbers?: boolean;
}

export const getPropsFromConfig = (config: ConfigProps) => {
    const {
        calendarClassName,
        monthClassName,
        weekDayClassName,
        dayClassName,
        selectedDay,
        shouldDayBeHidden,
        shouldDayBeDisabled,
        shouldDayBeHighlighted,
        showWeekNumbers = false,
    } = config;

    const today = getToday();

    return {
        selected: selectedDay,
        locale: mapToDateLocale(getContentLanguage()),
        showWeekNumbers,
        calendarStartDay: 1,
        inline: true,
        showPopperArrow: false,
        calendarClassName: classNames(calendarStyles.Calendar, calendarClassName),
        monthClassName: monthClassName ? () => monthClassName : undefined,
        weekDayClassName: weekDayClassName ? () => weekDayClassName : undefined,
        dayClassName: dayDate => {
            const isSelected = selectedDay && isSameDay(dayDate, selectedDay);
            const isToday = isSameDay(dayDate, today);
            const isHidden = shouldDayBeHidden?.(dayDate);
            const isDisabled = shouldDayBeDisabled?.(dayDate);
            const isHighlighted = shouldDayBeHighlighted?.(dayDate);

            return classNames(
                dayStyles.CalendarDay,
                isSelected && dayStyles['Variant--selected'],
                isToday && dayStyles['Variant--today'],
                isHidden && dayStyles['Variant--hidden'],
                isDisabled && dayStyles['Variant--disabled'],
                isHighlighted && dayStyles['Variant--highlighted'],
                dayClassName
            );
        },
    } satisfies Partial<ReactDatePickerProps>;
};
